import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleUnit, EquipmentCategory } from '../../types/schema';
import { usePMContext } from '../../contexts/PMContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { hasPermission, Permission } from '../../utils/permissions';
import { pmEquipmentConfigurationPath } from '../../utils/pmRoutes';
import { EmailNotificationSettings } from './EmailNotificationSettings';
import { NextScheduledServiceCard } from './NextScheduledServiceCard';
import { ServiceMatrixTable } from './ServiceMatrixTable';
import { AddServiceLevelModal } from './AddServiceLevelModal';
import { EditServiceLevelsModal } from './EditServiceLevelsModal';
import { AddToMatrixModal } from './AddToMatrixModal';
import { SaveAsTemplateModal } from './SaveAsTemplateModal';
import { RestoreMatrixSnapshotModal } from './RestoreMatrixSnapshotModal';
import { DeleteServiceLevelModal } from './DeleteServiceLevelModal';
import { RemoveMatrixItemModal } from './RemoveMatrixItemModal';
import { EquipmentUnsavedExitModal } from '../pages-equipment/EquipmentFormShared';
import {
    enrollUnitInPM,
    fetchPMConfigurationsByUnit,
    createServiceLevel,
    updateServiceLevel,
    deleteServiceLevel,
    fetchPMTemplatesByCategory,
    fetchPMMatrixSnapshots,
    createPMMatrixSnapshot,
    updatePartsAndProcedure,
} from '../../graphql/pmQueries';
import { listServiceItemsByCompanyId, createServiceItem } from '../../graphql/companyResourceQueries';
import type { PMPartsAndProcedure, PMTemplate, PMTemplateMatrixData } from '../../types/pmTypes';
import { pmSchedulingUM } from '../pages-equipment/equipmentFieldUtils';
import { displayUnitOfMeasure, normalizeUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import {
    nextMatrixGroupSortOrder,
    mergeMatrixAssignmentsOnRefresh,
    partitionMatrixRows,
    sortMatrixRows,
} from '../../utils/pmFormUtils';
import { formatStoredYearForDisplay } from '../../utils/dateInputUtils';
import { isUnitArchived } from '../pages-equipment/equipmentFieldUtils';
import {
    BackButton,
    Btn,
    Card,
    IconForm,
    IconPencil,
    IconPlus,
    PageHeader,
    SearchSuggest,
} from '../ui';
import type { SelectOption } from '../ui';

const sortByLabel = (a: { label: string }, b: { label: string }) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });

const sortByUnitName = (a: SingleUnit, b: SingleUnit) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });
import CardHeader from '../ui/headers/CardHeader';
import { displayPmMetaValue, PM_ENROLL_UNIT_PREFIX } from './pmConstants';
import {
    buildMatrixSnapshotData,
    levelIdsMissingFromSnapshot,
    parseMatrixSnapshotData,
    rewriteSnapshotAssignments,
    rowIdsMissingFromSnapshot,
} from '../../utils/pmMatrixSnapshot';
import '../../css/PMEquipmentConfiguration.css';

interface PMEquipmentConfigurationProps {
    unitId?: string;
    onBack: () => void;
    onPerformService: (unitId: string, serviceLevelIds: string[]) => void;
}

/**
 * PM Equipment Configuration — identity auto-fill, service matrix, templates, and overlays.
 */
export const PMEquipmentConfiguration = ({
    unitId,
    onBack,
    onPerformService,
}: PMEquipmentConfigurationProps) => {
    const navigate = useNavigate();
    const { pmConfigurations, enrolledUnits, setSelectedUnit, refreshPMData, getNextServiceLevel } =
        usePMContext();
    const { companyEquipmentCategories, companyUnits } = useEquipmentContext();
    const { companyUser, companyOwner, businessAdmin } = useUserContext();
    const canManage = hasPermission(companyOwner, companyUser, Permission.MANAGE_PM, businessAdmin);
    const { registerGuard, confirmPendingNavigation, cancelPendingNavigation } = useNavigationGuard();

    const [selectedCategoryId, setSelectedCategoryId] = useState<string>(() => {
        if (!unitId) return '';
        return (companyUnits.find((u: SingleUnit) => u.id === unitId)?.categoryId as string) || '';
    });
    const [selectedUnitId, setSelectedUnitId] = useState<string>(unitId ?? '');

    // Keep /pm-management/:unitId in sync when the picker enrolls or switches units.
    useEffect(() => {
        if (!selectedUnitId || selectedUnitId === unitId) return;
        navigate(pmEquipmentConfigurationPath(selectedUnitId), { replace: true });
    }, [selectedUnitId, unitId, navigate]);

    // Follow URL / parent when opening a different unit (queue click, deep link, back/forward).
    useEffect(() => {
        setSelectedUnitId(unitId ?? '');
        if (!unitId) return;
        const categoryId =
            (companyUnits.find((u: SingleUnit) => u.id === unitId)?.categoryId as string) || '';
        if (categoryId) setSelectedCategoryId(categoryId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to route unitId
    }, [unitId]);
    const [templates, setTemplates] = useState<PMTemplate[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const [isAddLevelOpen, setIsAddLevelOpen] = useState(false);
    const [isEditLevelsOpen, setIsEditLevelsOpen] = useState(false);
    const [isAddToMatrixOpen, setIsAddToMatrixOpen] = useState(false);
    const [addToMatrixDefaultTab, setAddToMatrixDefaultTab] = useState<'consumable' | 'procedure'>('consumable');
    const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
    const [latestSnapshot, setLatestSnapshot] = useState<{ id: string; createdAt?: string | null; matrixData?: unknown } | null>(null);
    const [showRestore, setShowRestore] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [deletingLevel, setDeletingLevel] = useState<{ id: string; name: string } | null>(null);
    const [removingItem, setRemovingItem] = useState<{ id: string; name: string } | null>(null);
    const [isDeletingLevel, setIsDeletingLevel] = useState(false);
    const [isRemovingItem, setIsRemovingItem] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [leaveViaGuard, setLeaveViaGuard] = useState(false);

    const [localAssignments, setLocalAssignments] = useState<Record<string, Record<string, boolean | number>>>({});
    const [matrixDirty, setMatrixDirty] = useState(false);
    const matrixDirtyRef = useRef(false);
    matrixDirtyRef.current = matrixDirty;
    const [qtyDrafts, setQtyDrafts] = useState<Record<string, string>>({});
    const [savingMatrix, setSavingMatrix] = useState(false);

    const [configParts, setConfigParts] = useState<PMPartsAndProcedure[]>([]);
    const [partsLoaded, setPartsLoaded] = useState(false);
    const [buildFromScratch, setBuildFromScratch] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState<PMTemplate | null>(null);
    const [keepingTemplate, setKeepingTemplate] = useState(false);
    const [matrixContainerEl, setMatrixContainerEl] = useState<HTMLDivElement | null>(null);
    const [matrixScrolls, setMatrixScrolls] = useState(false);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const currentUserId = companyUser?.id || companyOwner?.id || '';
    const currentUserName = companyUser?.name || companyOwner?.name || '';
    const units = enrolledUnits as SingleUnit[];
    const allCategories = companyEquipmentCategories as EquipmentCategory[];

    const filteredUnits = useMemo(() => {
        if (!selectedCategoryId) return [];
        return units.filter((u) => u.categoryId === selectedCategoryId);
    }, [units, selectedCategoryId]);

    const nonEnrolledForCategory = useMemo(() => {
        const enrolledIds = new Set(pmConfigurations.map((c) => c.unitId));
        let pool = companyUnits.filter((u: SingleUnit) => !isUnitArchived(u) && !enrolledIds.has(u.id));
        if (selectedCategoryId) pool = pool.filter((u: SingleUnit) => u.categoryId === selectedCategoryId);
        else pool = [];
        return pool;
    }, [companyUnits, pmConfigurations, selectedCategoryId]);

    const currentUnit = useMemo(
        () => companyUnits.find((u: SingleUnit) => u.id === selectedUnitId) ?? null,
        [companyUnits, selectedUnitId],
    );

    const currentConfig = useMemo(
        () => pmConfigurations.find((c) => c.unitId === selectedUnitId) ?? null,
        [pmConfigurations, selectedUnitId],
    );

    const currentCategory = useMemo(
        () => allCategories.find((c) => c.id === (currentUnit?.categoryId || selectedCategoryId)) ?? null,
        [allCategories, currentUnit, selectedCategoryId],
    );

    const unitOfMeasure = normalizeUnitOfMeasure(
        currentConfig?.unitOfMeasure ?? currentUnit?.unitOfMeasure ?? 'hours',
    );
    const unitOfMeasureLabel = displayUnitOfMeasure(unitOfMeasure);
    const serviceLevels = useMemo(
        () => [...(currentConfig?.serviceLevels ?? [])].sort((a, b) => a.intervalValue - b.intervalValue),
        [currentConfig],
    );
    const nextServiceLevel = currentConfig ? getNextServiceLevel(currentConfig.id) : null;
    const currentUM = pmSchedulingUM(currentUnit);
    const dueSoonPct = currentConfig?.dueSoonPercentage ?? 0.1;

    const yearDisplay = formatStoredYearForDisplay(currentUnit?.manufactureDate) || '—';

    const categoryOptions: SelectOption[] = useMemo(
        () =>
            [...allCategories]
                .map((cat) => ({ value: cat.id, label: cat.name || '' }))
                .sort(sortByLabel),
        [allCategories],
    );

    const knownLevelNames = useMemo(() => {
        const names = new Set<string>();
        for (const config of pmConfigurations) {
            for (const level of config.serviceLevels ?? []) {
                const name = level.levelIdentifier?.trim();
                if (name) names.add(name);
            }
        }
        return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [pmConfigurations]);

    const isEmptyConfig = serviceLevels.length === 0 && configParts.length === 0;
    const showTemplatePicker = Boolean(
        canManage && currentConfig && partsLoaded && !buildFromScratch && isEmptyConfig,
    );
    const showMatrixEditor = Boolean(currentConfig && partsLoaded && (!isEmptyConfig || buildFromScratch));

    // The scroll hint only applies when the DataTable scrollport is taller than its visible area.
    useEffect(() => {
        if (!matrixContainerEl) {
            setMatrixScrolls(false);
            return;
        }
        const scrollEl =
            matrixContainerEl.querySelector<HTMLElement>('.data-table-wrapper.is-scrollable') ??
            matrixContainerEl;
        const measure = () => {
            setMatrixScrolls(scrollEl.scrollHeight > scrollEl.clientHeight + 1);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(scrollEl);
        const table = scrollEl.querySelector('table');
        if (table) observer.observe(table);
        return () => observer.disconnect();
    }, [matrixContainerEl, configParts.length, serviceLevels.length]);

    const unitOptions: SelectOption[] = useMemo(() => {
        const enrolled = [...filteredUnits]
            .sort(sortByUnitName)
            .map((u: SingleUnit) => ({ value: u.id, label: u.name || '' }));
        const enroll = canManage
            ? [...nonEnrolledForCategory]
                  .sort(sortByUnitName)
                  .map((u: SingleUnit) => ({
                      value: `${PM_ENROLL_UNIT_PREFIX}${u.id}`,
                      label: `+ ${u.name} (enroll)`,
                  }))
            : [];
        return [...enrolled, ...enroll];
    }, [filteredUnits, nonEnrolledForCategory, canManage]);

    const fetchConfigParts = useCallback(async (isCancelled?: () => boolean, options?: { silent?: boolean }) => {
        if (!currentConfig || !companyId) {
            if (!isCancelled?.()) {
                setConfigParts([]);
                setPartsLoaded(true);
            }
            return;
        }
        if (!options?.silent && !isCancelled?.()) setPartsLoaded(false);
        try {
            const allItems = await listServiceItemsByCompanyId(companyId);
            if (isCancelled?.()) return;
            setConfigParts(
                sortMatrixRows(
                    allItems.filter(
                        (item: PMPartsAndProcedure) =>
                            item.pmConfigurationIds && item.pmConfigurationIds.includes(currentConfig.id),
                    ),
                ),
            );
            setPartsLoaded(true);
        } catch (err) {
            if (!isCancelled?.()) {
                console.error('Failed to fetch config parts:', err);
                setPartsLoaded(true);
            }
        }
    }, [currentConfig, companyId]);

    useEffect(() => {
        let isCancelled = false;
        void fetchConfigParts(() => isCancelled);
        return () => {
            isCancelled = true;
        };
    }, [fetchConfigParts]);

    useEffect(() => {
        if (!currentConfig?.id) {
            setLatestSnapshot(null);
            return;
        }
        let isCancelled = false;
        void fetchPMMatrixSnapshots(currentConfig.id)
            .then((items) => {
                if (isCancelled) return;
                const newest = [...items]
                    .filter((item) => item?.id)
                    .sort((a, b) => (b?.createdAt || '').localeCompare(a?.createdAt || ''))[0];
                setLatestSnapshot(
                    newest
                        ? { id: newest.id, createdAt: newest.createdAt, matrixData: newest.matrixData }
                        : null,
                );
            })
            .catch((err) => {
                if (!isCancelled) console.error('Failed to load the saved matrix copy:', err);
            });
        return () => {
            isCancelled = true;
        };
    }, [currentConfig?.id]);

    useEffect(() => {
        const dirty = matrixDirtyRef.current;
        setLocalAssignments((prev) => mergeMatrixAssignmentsOnRefresh(serviceLevels, prev, dirty));
        if (!dirty) setQtyDrafts({});
    }, [serviceLevels]);

    useEffect(() => {
        if (currentUnit) setSelectedUnit(currentUnit);
    }, [currentUnit, setSelectedUnit]);

    useEffect(() => {
        if (unitId && !selectedCategoryId) {
            const unit = companyUnits.find((u: SingleUnit) => u.id === unitId);
            if (unit?.categoryId) setSelectedCategoryId(unit.categoryId);
        }
    }, [unitId, companyUnits, selectedCategoryId]);

    useEffect(() => {
        setBuildFromScratch(false);
        setPreviewTemplate(null);
        setPartsLoaded(false);
    }, [currentConfig?.id]);

    useEffect(() => {
        if (!showTemplatePicker || !currentCategory?.id) return;

        let isCancelled = false;
        setLoadingTemplates(true);
        fetchPMTemplatesByCategory(currentCategory.id)
            .then((items) => {
                if (isCancelled) return;
                setTemplates(
                    items.map((t: PMTemplate & { matrixData: string | PMTemplateMatrixData }) => ({
                        ...t,
                        matrixData: typeof t.matrixData === 'string' ? JSON.parse(t.matrixData) : t.matrixData,
                    })),
                );
            })
            .catch((err) => {
                if (!isCancelled) console.error(err);
            })
            .finally(() => {
                if (!isCancelled) setLoadingTemplates(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [showTemplatePicker, currentCategory]);

    useEffect(() => {
        registerGuard(
            matrixDirty
                ? {
                      when: true,
                      onBlocked: () => {
                          setLeaveViaGuard(true);
                          setShowExitModal(true);
                      },
                  }
                : null,
        );
        return () => registerGuard(null);
    }, [matrixDirty, registerGuard]);

    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setSelectedUnitId('');
    };

    const handleEnrollAndConfigure = async (newUnitId: string) => {
        if (!companyId || !newUnitId) return;
        try {
            const existing = await fetchPMConfigurationsByUnit(newUnitId);
            if (existing && existing.length > 0) {
                setSelectedUnitId(newUnitId);
                return;
            }
            const unit = companyUnits.find((u: SingleUnit) => u.id === newUnitId);
            await enrollUnitInPM({
                companyId,
                unitId: newUnitId,
                unitOfMeasure: normalizeUnitOfMeasure(unit?.unitOfMeasure ?? 'hours'),
                dueSoonPercentage: 0.1,
            });
            await refreshPMData();
            setBuildFromScratch(false);
            setSelectedUnitId(newUnitId);
        } catch (err) {
            console.error('Enrollment failed:', err);
        }
    };

    const handleUnitChange = (newUnitId: string) => {
        if (canManage && newUnitId.startsWith(PM_ENROLL_UNIT_PREFIX)) {
            void handleEnrollAndConfigure(newUnitId.slice(PM_ENROLL_UNIT_PREFIX.length));
            return;
        }
        setSelectedUnitId(newUnitId);
    };

    const handleAddLevel = async (data: { levelName: string; targetUM: number; unit: string }) => {
        if (!currentConfig) return;
        try {
            await createServiceLevel({
                pmConfigurationId: currentConfig.id,
                levelIdentifier: data.levelName,
                intervalValue: data.targetUM,
                targetUM: data.targetUM,
            });
            await refreshPMData();
        } catch (err) {
            console.error('Failed to add level:', err);
        }
    };

    const handleSaveLevels = async (
        rows: { id: string; levelIdentifier: string; intervalValue: number }[],
    ) => {
        await Promise.all(
            rows.map((row) =>
                updateServiceLevel({
                    id: row.id,
                    levelIdentifier: row.levelIdentifier,
                    intervalValue: row.intervalValue,
                    targetUM: row.intervalValue,
                }),
            ),
        );
        await refreshPMData();
    };

    const handleAddItemToMatrix = async (item: PMPartsAndProcedure) => {
        if (!currentConfig) return;
        try {
            const existingIds = item.pmConfigurationIds || [];
            if (!existingIds.includes(currentConfig.id)) {
                await updatePartsAndProcedure({
                    id: item.id,
                    pmConfigurationIds: [...existingIds, currentConfig.id],
                    sortOrder: nextMatrixGroupSortOrder(configParts, item.itemType),
                } as Parameters<typeof updatePartsAndProcedure>[0]);
            }
            await fetchConfigParts(undefined, { silent: true });
            window.setTimeout(() => {
                document.getElementById(`pm-matrix-row-${item.id}`)?.scrollIntoView({ block: 'nearest' });
            }, 0);
        } catch (err) {
            console.error('Failed to add item to matrix:', err);
        }
    };

    const handleMatrixCellToggle = (levelId: string, partId: string, itemType: string) => {
        setLocalAssignments((prev) => {
            const levelData = { ...(prev[levelId] || {}) };
            if (itemType === 'procedure') {
                levelData[partId] = !levelData[partId];
            } else {
                const current = levelData[partId];
                levelData[partId] = current ? false : 1;
            }
            return { ...prev, [levelId]: levelData };
        });
        setMatrixDirty(true);
    };

    const handleMatrixQtyChange = (levelId: string, partId: string, qty: number) => {
        setLocalAssignments((prev) => {
            const levelData = { ...(prev[levelId] || {}) };
            levelData[partId] = qty > 0 ? qty : false;
            return { ...prev, [levelId]: levelData };
        });
        setMatrixDirty(true);
    };

    const handleSaveConfiguration = async () => {
        if (!currentConfig) return;
        setSavingMatrix(true);
        try {
            const promises = serviceLevels.map((sl) =>
                updateServiceLevel({
                    id: sl.id,
                    serviceLevelAssignments: JSON.stringify(localAssignments[sl.id] || {}),
                }),
            );
            await Promise.all(promises);
            const snapshot = buildMatrixSnapshotData(serviceLevels, configParts, localAssignments);
            const saved = await createPMMatrixSnapshot({
                companyId,
                pmConfigurationId: currentConfig.id,
                unitId: currentConfig.unitId,
                matrixData: JSON.stringify(snapshot),
                createdByUserId: currentUserId || undefined,
                createdByUserName: currentUserName || undefined,
            });
            if (saved?.id) {
                setLatestSnapshot({ id: saved.id, createdAt: saved.createdAt, matrixData: saved.matrixData });
            }
            await refreshPMData();
            setMatrixDirty(false);
        } catch (err) {
            console.error('Failed to save:', err);
        } finally {
            setSavingMatrix(false);
        }
    };

    const handleConfirmRestore = async () => {
        if (!currentConfig || !latestSnapshot || !companyId) return;
        const data = parseMatrixSnapshotData(latestSnapshot.matrixData);
        if (!data) return;
        setIsRestoring(true);
        try {
            const catalog = await listServiceItemsByCompanyId(companyId);
            const byId = new Map(catalog.map((item) => [item.id, item]));
            const partIdMap: Record<string, string> = {};

            for (const row of data.rows) {
                const existing = byId.get(row.id);
                if (existing) {
                    const ids = existing.pmConfigurationIds || [];
                    if (!ids.includes(currentConfig.id)) {
                        await updatePartsAndProcedure({
                            id: existing.id,
                            pmConfigurationIds: [...ids, currentConfig.id],
                            sortOrder: row.sortOrder ?? undefined,
                        } as Parameters<typeof updatePartsAndProcedure>[0]);
                    }
                } else {
                    const created = await createServiceItem({
                        companyId,
                        itemName: row.itemName,
                        itemType: row.itemType,
                        category: row.category,
                        manufacturer: row.manufacturer,
                        partNumber: row.partNumber,
                        vendorName: row.vendorName,
                        price: row.price ?? undefined,
                        quantity: row.quantity ?? undefined,
                        unitOfMeasure: row.unitOfMeasure,
                        sortOrder: row.sortOrder ?? undefined,
                        pmConfigurationIds: [currentConfig.id],
                    });
                    if (created?.id) partIdMap[row.id] = created.id;
                }
            }

            for (const rowId of rowIdsMissingFromSnapshot(configParts.map((part) => part.id), data)) {
                const item = configParts.find((part) => part.id === rowId);
                if (!item) continue;
                await updatePartsAndProcedure({
                    id: rowId,
                    pmConfigurationIds: (item.pmConfigurationIds || []).filter((id) => id !== currentConfig.id),
                } as Parameters<typeof updatePartsAndProcedure>[0]);
            }

            const currentLevelIds = new Set(serviceLevels.map((level) => level.id));
            for (const levelId of levelIdsMissingFromSnapshot(serviceLevels.map((level) => level.id), data)) {
                await deleteServiceLevel(levelId);
            }

            for (const level of data.levels) {
                const assignments = JSON.stringify(rewriteSnapshotAssignments(level.assignments || {}, partIdMap));
                if (currentLevelIds.has(level.id)) {
                    await updateServiceLevel({
                        id: level.id,
                        levelIdentifier: level.levelIdentifier,
                        intervalValue: level.intervalValue,
                        targetUM: level.targetUM,
                        description: level.description,
                        serviceLevelAssignments: assignments,
                    });
                } else {
                    await createServiceLevel({
                        pmConfigurationId: currentConfig.id,
                        levelIdentifier: level.levelIdentifier,
                        intervalValue: level.intervalValue,
                        targetUM: level.targetUM,
                        description: level.description,
                        serviceLevelAssignments: assignments,
                    });
                }
            }

            await refreshPMData();
            await fetchConfigParts(undefined, { silent: true });
            setMatrixDirty(false);
            setShowRestore(false);
        } catch (err) {
            console.error('Failed to put the matrix back:', err);
        } finally {
            setIsRestoring(false);
        }
    };

    const handleApplyTemplate = async (template: PMTemplate) => {
        if (!currentConfig) return;
        const data = template.matrixData as PMTemplateMatrixData;
        try {
            for (let i = 0; i < data.levels.length; i++) {
                const level = data.levels[i];
                await createServiceLevel({
                    pmConfigurationId: currentConfig.id,
                    levelIdentifier: level.levelIdentifier,
                    intervalValue: level.intervalValue,
                    targetUM: level.targetUM,
                    description: level.description,
                });
            }

            if (data.rows && data.rows.length > 0) {
                const existingItems = await listServiceItemsByCompanyId(companyId);

                for (const row of data.rows) {
                    let item = existingItems.find(
                        (ei: PMPartsAndProcedure) => ei.itemName.toLowerCase() === row.itemName.toLowerCase(),
                    );

                    if (item) {
                        const existingIds = item.pmConfigurationIds || [];
                        if (!existingIds.includes(currentConfig.id)) {
                            await updatePartsAndProcedure({
                                id: item.id,
                                pmConfigurationIds: [...existingIds, currentConfig.id],
                            } as Parameters<typeof updatePartsAndProcedure>[0]);
                        }
                    } else {
                        await createServiceItem({
                            companyId,
                            itemName: row.itemName,
                            itemType: row.itemType === 'procedure' ? 'Procedure' : 'Consumable',
                            category: row.category || undefined,
                            manufacturer: row.manufacturer || undefined,
                            partNumber: row.partNumber || undefined,
                            vendorName: row.vendorName || undefined,
                            price: row.price ?? undefined,
                            quantity: row.quantity ?? undefined,
                            unitOfMeasure: row.unitOfMeasure || undefined,
                            pmConfigurationIds: [currentConfig.id],
                        });
                    }
                }
            }

            await refreshPMData();
            await fetchConfigParts();
        } catch (err) {
            console.error('Failed to apply template:', err);
        }
    };

    const buildMatrixDataJson = (): string => {
        const levels = serviceLevels.map((sl) => ({
            levelIdentifier: sl.levelIdentifier,
            intervalValue: sl.intervalValue,
            targetUM: sl.targetUM,
            description: sl.description || undefined,
        }));
        const rows = configParts.map((p) => ({
            itemName: p.itemName,
            itemType: (p.itemType || 'consumable') as 'consumable' | 'procedure',
            category: p.category || undefined,
            manufacturer: p.manufacturer || undefined,
            partNumber: p.partNumber || undefined,
            vendorName: p.vendorName || undefined,
            price: p.price ?? undefined,
            quantity: p.quantity ?? undefined,
            unitOfMeasure: p.unitOfMeasure || undefined,
        }));
        const matrixData: PMTemplateMatrixData = { levels, rows, assignments: localAssignments };
        return JSON.stringify(matrixData);
    };

    const handleMatrixRowReorder = async (activeId: string, overId: string) => {
        const from = configParts.findIndex((part) => part.id === activeId);
        const to = configParts.findIndex((part) => part.id === overId);
        if (from < 0 || to < 0 || from === to) return;

        const reordered = [...configParts];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        const grouped = partitionMatrixRows(reordered);
        setConfigParts(grouped);
        setMatrixDirty(true);

        for (let i = 0; i < grouped.length; i++) {
            if (grouped[i].sortOrder !== i) {
                await updatePartsAndProcedure({
                    id: grouped[i].id,
                    sortOrder: i,
                } as Parameters<typeof updatePartsAndProcedure>[0]);
            }
        }
    };

    const handleConfirmRemoveItem = async () => {
        if (!currentConfig || !removingItem) return;
        const item = configParts.find((p) => p.id === removingItem.id);
        if (!item) return;
        setIsRemovingItem(true);
        try {
            const updatedIds = (item.pmConfigurationIds || []).filter((cid) => cid !== currentConfig.id);
            await updatePartsAndProcedure({
                id: removingItem.id,
                pmConfigurationIds: updatedIds,
            } as Parameters<typeof updatePartsAndProcedure>[0]);
            await refreshPMData();
            await fetchConfigParts();
            setRemovingItem(null);
        } catch (err) {
            console.error('Failed to remove item:', err);
        } finally {
            setIsRemovingItem(false);
        }
    };

    const handleConfirmDeleteLevel = async () => {
        if (!deletingLevel) return;
        setIsDeletingLevel(true);
        try {
            await deleteServiceLevel(deletingLevel.id);
            await refreshPMData();
            setDeletingLevel(null);
        } catch (err) {
            console.error('Failed to delete level:', err);
        } finally {
            setIsDeletingLevel(false);
        }
    };

    const handleBack = () => {
        if (matrixDirty) {
            setLeaveViaGuard(false);
            setShowExitModal(true);
            return;
        }
        onBack();
    };

    const handleConfirmLeave = () => {
        setShowExitModal(false);
        setMatrixDirty(false);
        registerGuard(null);
        if (leaveViaGuard) {
            confirmPendingNavigation();
            return;
        }
        onBack();
    };

    const canSave = Boolean(canManage && currentUnit && currentConfig && showMatrixEditor);

    return (
        <div className="pm-config-container">
            <BackButton onClick={handleBack} buttonText="Back to PM Dashboard" />

            <PageHeader
                title="PM Equipment Configuration"
                subtitle={currentUnit?.name ? `Unit ID: ${currentUnit.name}` : undefined}
                className="pm-config-page-header"
                actions={
                    canSave ? (
                        <div className="pm-config-header-actions">
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Save as Template"
                                buttonOnClick={() => setIsSaveTemplateOpen(true)}
                            />
                            {latestSnapshot?.createdAt && (
                                <Btn
                                    variant="secondary-outlined"
                                    buttonText="Put back last save"
                                    buttonOnClick={() => setShowRestore(true)}
                                    buttonDisabled={savingMatrix || isRestoring}
                                />
                            )}
                            <Btn
                                variant="primary"
                                buttonText={savingMatrix ? 'Saving...' : 'Save Configuration'}
                                buttonOnClick={() => {
                                    void handleSaveConfiguration();
                                }}
                                buttonDisabled={savingMatrix}
                            />
                        </div>
                    ) : undefined
                }
            />

            <Card variant="card-lg" className="pm-config-identity-card card-pad-lg">
                <div className="pm-config-dropdowns">
                    <label className="pm-config-dropdown-group">
                        <span className="pm-config-dropdown-label">Category</span>
                        <SearchSuggest
                            options={categoryOptions}
                            value={selectedCategoryId}
                            onChange={handleCategoryChange}
                            placeholder="Select category"
                            emptyMessage="No matching categories"
                            ariaLabel="Equipment category"
                            inputId="pm-config-category"
                        />
                    </label>
                    <label className="pm-config-dropdown-group">
                        <span className="pm-config-dropdown-label">Unit number</span>
                        <SearchSuggest
                            options={unitOptions}
                            value={selectedUnitId}
                            onChange={handleUnitChange}
                            placeholder="Select unit"
                            emptyMessage={selectedCategoryId ? 'No units in this category' : 'Select a category first'}
                            ariaLabel="Equipment unit"
                            inputId="pm-config-unit"
                        />
                    </label>
                </div>

                {currentUnit && (
                    <div className="pm-config-meta">
                        <div className="pm-config-meta-item">
                            <span className="pm-config-meta-label">Manufacturer</span>
                            <span className="pm-config-meta-value">
                                {displayPmMetaValue(currentUnit.manufacturerName)}
                            </span>
                        </div>
                        <div className="pm-config-meta-item">
                            <span className="pm-config-meta-label">Model #</span>
                            <span className="pm-config-meta-value">
                                {displayPmMetaValue(currentUnit.modelNumber)}
                            </span>
                        </div>
                        <div className="pm-config-meta-item">
                            <span className="pm-config-meta-label">Year</span>
                            <span className="pm-config-meta-value">{yearDisplay}</span>
                        </div>
                        <div className="pm-config-meta-item">
                            <span className="pm-config-meta-label">Engine manuf.</span>
                            <span className="pm-config-meta-value">
                                {displayPmMetaValue(currentUnit.engineManufacturer)}
                            </span>
                        </div>
                        <div className="pm-config-meta-item">
                            <span className="pm-config-meta-label">Engine model #</span>
                            <span className="pm-config-meta-value">
                                {displayPmMetaValue(currentUnit.engineModelNumber)}
                            </span>
                        </div>
                    </div>
                )}

                {showTemplatePicker && !previewTemplate && (
                    <div className="pm-template-start">
                        <p className="pm-template-picker-title">How do you want to start?</p>
                        <div className="pm-template-picker-options">
                            <button
                                type="button"
                                className="pm-template-option-card pm-template-option-card--template"
                                onClick={() => setBuildFromScratch(false)}
                            >
                                <span className="pm-template-option-icon pm-template-option-icon--template" aria-hidden="true">
                                    <IconForm />
                                </span>
                                <span className="pm-template-option-copy">
                                    <span className="pm-template-option-title">Start from a template</span>
                                    <span className="pm-template-option-desc">
                                        Load a saved PM matrix for this category, then edit and save.
                                    </span>
                                    {templates.length > 0 && (
                                        <span className="pm-template-option-count">
                                            {templates.length} template{templates.length !== 1 ? 's' : ''} for{' '}
                                            {currentCategory?.name || 'this category'} →
                                        </span>
                                    )}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="pm-template-option-card pm-template-option-card--scratch"
                                onClick={() => setBuildFromScratch(true)}
                            >
                                <span className="pm-template-option-icon pm-template-option-icon--scratch" aria-hidden="true">
                                    <IconPlus />
                                </span>
                                <span className="pm-template-option-copy">
                                    <span className="pm-template-option-title">Build from scratch</span>
                                    <span className="pm-template-option-desc">
                                        Start with an empty matrix and add levels, parts, and procedures yourself.
                                    </span>
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {!currentUnit && (
                <p className="pm-config-empty">
                    Select an equipment category and unit to view or configure its PM schedule.
                </p>
            )}

            {currentUnit && !currentConfig && (
                <p className="pm-config-empty">No PM configuration found for this unit.</p>
            )}

            {currentUnit && currentConfig && partsLoaded && isEmptyConfig && !canManage && (
                <p className="pm-config-empty">This unit does not have a PM matrix yet.</p>
            )}

            {currentUnit && currentConfig && !partsLoaded && (
                <p className="pm-config-empty">Loading configuration...</p>
            )}

            {currentUnit && currentConfig && showTemplatePicker && !previewTemplate && (
                <Card variant="card-lg" className="pm-template-picker card-pad-lg">
                    <div className="pm-template-list">
                        <div className="pm-template-list-header">
                            <div>
                                <h4 className="pm-template-list-title">Choose a template</h4>
                                <p className="pm-template-list-note">
                                    Templates are shared across every unit in this category.
                                </p>
                            </div>
                            {currentCategory?.name && (
                                <span className="pm-template-list-category">{currentCategory.name}</span>
                            )}
                        </div>
                        {loadingTemplates && <p className="pm-config-empty">Loading templates...</p>}
                        {!loadingTemplates && templates.length === 0 && (
                            <p className="pm-config-empty">No templates saved for this category yet.</p>
                        )}
                        {templates.map((t) => (
                            <div key={t.id} className="pm-template-item">
                                <div className="pm-template-item-info">
                                    <p className="pm-template-item-name">{t.name}</p>
                                    {t.description && <p className="pm-template-item-desc">{t.description}</p>}
                                </div>
                                <div className="pm-template-item-meta">
                                    <span className="pm-template-item-pill">
                                        {t.matrixData.levels?.length || 0} levels
                                    </span>
                                    <span className="pm-template-item-pill">
                                        {t.matrixData.rows?.length || 0} items
                                    </span>
                                </div>
                                <Btn
                                    variant="primary"
                                    buttonSize="sm"
                                    buttonText="Review"
                                    buttonOnClick={() => setPreviewTemplate(t)}
                                />
                            </div>
                        ))}
                        <p className="pm-template-scratch-link">
                            Don&apos;t see a fit?{' '}
                            <Btn
                                variant="link"
                                buttonText="Build from scratch instead →"
                                buttonOnClick={() => setBuildFromScratch(true)}
                            />
                        </p>
                    </div>
                </Card>
            )}

            {currentUnit && currentConfig && showTemplatePicker && previewTemplate && (
                <Card variant="card-lg" className="pm-template-picker card-pad-lg">
                    <div className="pm-template-list">
                        <div className="pm-template-list-header">
                            <div>
                                <h4 className="pm-template-list-title">{previewTemplate.name}</h4>
                                <p className="pm-template-list-note">
                                    This is a preview. Open another template before you keep one, or keep this one and then edit it.
                                </p>
                            </div>
                        </div>
                        {previewTemplate.description && (
                            <p className="pm-template-item-desc">{previewTemplate.description}</p>
                        )}
                        <div className="pm-template-preview-levels">
                            {(previewTemplate.matrixData.levels ?? []).map((level) => (
                                <span key={level.levelIdentifier} className="pm-template-item-pill">
                                    {level.levelIdentifier}
                                    {level.intervalValue ? ` · ${level.intervalValue}` : ''}
                                </span>
                            ))}
                        </div>
                        <ul className="pm-template-preview-items">
                            {(previewTemplate.matrixData.rows ?? []).map((row, index) => (
                                <li key={`${row.itemName}-${index}`}>
                                    {row.itemName}
                                    {row.itemType === 'procedure' ? ' · Procedure' : ''}
                                </li>
                            ))}
                        </ul>
                        <div className="pm-template-preview-actions">
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Back to templates"
                                buttonOnClick={() => setPreviewTemplate(null)}
                                buttonDisabled={keepingTemplate}
                            />
                            <Btn
                                variant="primary"
                                buttonText={keepingTemplate ? 'Keeping…' : 'Keep this template'}
                                buttonDisabled={keepingTemplate}
                                buttonOnClick={() => {
                                    void (async () => {
                                        setKeepingTemplate(true);
                                        try {
                                            await handleApplyTemplate(previewTemplate);
                                            setPreviewTemplate(null);
                                        } finally {
                                            setKeepingTemplate(false);
                                        }
                                    })();
                                }}
                            />
                        </div>
                    </div>
                </Card>
            )}

            {currentUnit && currentConfig && showMatrixEditor && (
                <>
                    <NextScheduledServiceCard
                        nextServiceInfo={nextServiceLevel}
                        currentUM={currentUM}
                        unitOfMeasure={unitOfMeasure}
                        dueSoonPercentage={dueSoonPct}
                        onPerformService={(serviceLevelIds) => {
                            if (selectedUnitId) onPerformService(selectedUnitId, serviceLevelIds);
                        }}
                    />
                    <Card variant="card-lg" className="pm-config-matrix-card">
                        <CardHeader
                            as="h3"
                            title="Service Matrix"
                            paddingVariant="pad-lg"
                            actions={
                                <div className="pm-matrix-legend">
                                    <span className="pm-matrix-item-type pm-matrix-item-type--con">
                                        $ Consumable
                                    </span>
                                    <span className="pm-matrix-legend-hint">qty per level</span>
                                    <span className="pm-matrix-item-type pm-matrix-item-type--proc">
                                        Procedure
                                    </span>
                                    <span className="pm-matrix-legend-hint">check to perform</span>
                                    <span className="pm-matrix-legend-hint pm-matrix-legend-hint--inherited">
                                        Nested lower-level items autofill higher columns (read-only)
                                    </span>
                                    {canManage && serviceLevels.length > 0 && (
                                        <Btn
                                            variant="secondary-outlined"
                                            buttonSize="sm"
                                            buttonIcon={<IconPencil />}
                                            buttonText="Edit Levels"
                                            buttonClasses="pm-matrix-edit-levels-btn"
                                            buttonOnClick={() => setIsEditLevelsOpen(true)}
                                        />
                                    )}
                                </div>
                            }
                        />

                        <ServiceMatrixTable
                            parts={configParts}
                            serviceLevels={serviceLevels}
                            localAssignments={localAssignments}
                            qtyDrafts={qtyDrafts}
                            setQtyDrafts={setQtyDrafts}
                            unitOfMeasure={unitOfMeasure}
                            unitOfMeasureLabel={unitOfMeasureLabel}
                            canManage={canManage}
                            onToggleProcedure={(levelId, partId) =>
                                handleMatrixCellToggle(levelId, partId, 'procedure')
                            }
                            onQtyChange={handleMatrixQtyChange}
                            onAddLevel={() => setIsAddLevelOpen(true)}
                            onRemovePart={(part) => setRemovingItem(part)}
                            onReorderParts={(activeId, overId) => {
                                void handleMatrixRowReorder(activeId, overId);
                            }}
                            scrollWrapperRef={setMatrixContainerEl}
                        />

                                {canManage && (
                                <div className="pm-matrix-footer-actions">
                                    {configParts.length > 0 && matrixScrolls && (
                                        <p className="pm-matrix-scroll-hint">
                                            Scroll to view more consumables and procedures.
                                        </p>
                                    )}
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonIcon={<IconPlus />}
                                        buttonText="Add Consumable"
                                        buttonOnClick={() => {
                                            setAddToMatrixDefaultTab('consumable');
                                            setIsAddToMatrixOpen(true);
                                        }}
                                    />
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonIcon={<IconPlus />}
                                        buttonText="Add Procedure"
                                        buttonClasses="pm-matrix-add-procedure-btn"
                                        buttonOnClick={() => {
                                            setAddToMatrixDefaultTab('procedure');
                                            setIsAddToMatrixOpen(true);
                                        }}
                                    />
                                </div>
                                )}
                    </Card>

                    <EmailNotificationSettings
                        readOnly={!canManage}
                        notificationSettings={currentConfig.notificationSettings ?? []}
                        pmConfigurationId={currentConfig.id}
                        companyId={companyId}
                        companyOwnerId={companyOwner?.id}
                        unitOfMeasure={unitOfMeasure}
                        onDataChange={refreshPMData}
                    />
                </>
            )}

            <AddServiceLevelModal
                isOpen={isAddLevelOpen}
                onClose={() => setIsAddLevelOpen(false)}
                onAdd={handleAddLevel}
                defaultUnit={unitOfMeasure}
                existingLevels={serviceLevels.map((sl) => ({
                    levelIdentifier: sl.levelIdentifier,
                    intervalValue: sl.intervalValue,
                }))}
                knownLevelNames={knownLevelNames}
            />

            <EditServiceLevelsModal
                isOpen={isEditLevelsOpen}
                onClose={() => setIsEditLevelsOpen(false)}
                onSave={handleSaveLevels}
                onRemoveLevel={(level) => {
                    setIsEditLevelsOpen(false);
                    setDeletingLevel(level);
                }}
                levels={serviceLevels}
                unitOfMeasure={unitOfMeasure}
            />

            <AddToMatrixModal
                isOpen={isAddToMatrixOpen}
                onClose={() => setIsAddToMatrixOpen(false)}
                onAddItem={handleAddItemToMatrix}
                companyId={companyId}
                excludeIds={configParts.map((p) => p.id)}
                defaultTab={addToMatrixDefaultTab}
            />

            <RestoreMatrixSnapshotModal
                savedAt={showRestore ? latestSnapshot?.createdAt || null : null}
                isRestoring={isRestoring}
                onDismiss={() => {
                    if (!isRestoring) setShowRestore(false);
                }}
                onConfirmRestore={() => {
                    void handleConfirmRestore();
                }}
            />

            <SaveAsTemplateModal
                isOpen={isSaveTemplateOpen}
                onClose={() => setIsSaveTemplateOpen(false)}
                onSaved={() => undefined}
                companyId={companyId}
                categoryId={currentCategory?.id || ''}
                categoryName={currentCategory?.name || ''}
                matrixDataJson={buildMatrixDataJson()}
                userId={currentUserId}
                userName={currentUserName}
            />

            <DeleteServiceLevelModal
                level={deletingLevel}
                isDeleting={isDeletingLevel}
                onDismiss={() => setDeletingLevel(null)}
                onConfirmDelete={() => {
                    void handleConfirmDeleteLevel();
                }}
            />

            <RemoveMatrixItemModal
                item={removingItem}
                isRemoving={isRemovingItem}
                onDismiss={() => setRemovingItem(null)}
                onConfirmRemove={() => {
                    void handleConfirmRemoveItem();
                }}
            />

            <EquipmentUnsavedExitModal
                visible={showExitModal}
                onDismiss={() => {
                    setShowExitModal(false);
                    cancelPendingNavigation();
                }}
                onConfirmLeave={handleConfirmLeave}
            />
        </div>
    );
};
